/**
 * Liminal Reverb — switchable room-preview toys.
 * Camera (one monitor), Monitors (five selectable screens), photo booth.
 * One shared engine: sample + room + dry carry across the switcher.
 *
 * Audio + state: LiminalPreviewEngine (assets/lr-preview-engine.js)
 * File registration: assets/lr-room-library.js (wet: sample × room, dry: sample)
 */
(function () {
  function assetBase() {
    var node = document.getElementById('lr-proto-asset-base');
    if (!node) return '';
    try {
      var value = JSON.parse(node.textContent || '""');
      return typeof value === 'string' ? value : '';
    } catch (err) {
      return '';
    }
  }

  function fmt(s) {
    if (!isFinite(s) || s < 0) s = 0;
    var m = Math.floor(s / 60);
    var r = Math.floor(s % 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var INK = 'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"';

  var SAMPLE_MARKS = {
    /* guitar: Superstrat double-cutaway, slim and long */
    'sample-1':
      '<svg viewBox="0 0 24 24" aria-hidden="true"><g ' + INK + '>' +
      '<g transform="rotate(45 12 12)">' +
      '<path d="M11 12.6C10.2 12.6 9.2 12.2 8.2 11C6.8 12.2 7.4 14.4 7.8 15.8C6.2 16.4 5.2 17.8 5.8 19.8C6.6 21.8 9 23 12 23C15 23 17.2 21.6 17.6 19.6C18 17.8 16.6 16.4 15.2 15.8C16 14.6 16.6 13.2 15.4 12C14.4 12.6 13.6 12.6 12.8 12.6C12.2 13 11.6 13 11 12.6Z"/>' +
      '<path d="M10.9 12.6V1.4M13.1 12.6V1.4"/>' +
      '<rect x="10.7" y="14.6" width="2.6" height=".95" rx=".25"/>' +
      '<rect x="11.1" y="16" width="1.8" height=".6" rx=".2"/>' +
      '<rect x="10.7" y="17" width="2.6" height=".95" rx=".25"/>' +
      '<path d="M10.2 19h3.6"/>' +
      '<rect x="10.1" y="-1.8" width="3.8" height="3.4" rx=".8"/>' +
      '</g></g></svg>',
    /* synth: a keyboard, black keys hanging from the top edge */
    'sample-2':
      '<svg viewBox="0 0 24 24" aria-hidden="true"><g ' + INK + '>' +
      '<rect x="2" y="6" width="20" height="12" rx="1.6"/>' +
      '<path d="M14 6v12"/>' +
      '<path d="M6 13v5M10 13v5M18 13v5"/>' +
      '<rect x="4.7" y="6" width="2.6" height="7" fill="currentColor"/>' +
      '<rect x="8.7" y="6" width="2.6" height="7" fill="currentColor"/>' +
      '<rect x="16.7" y="6" width="2.6" height="7" fill="currentColor"/>' +
      '</g></svg>',
    /* full mix: a waveform */
    'sample-5':
      '<svg viewBox="0 0 24 24" aria-hidden="true"><g ' + INK + '>' +
      '<path d="M3 9.5v5M6 7v10M9 4.5v15M12 2.5v19M15 5.5v13M18 8v8M21 10v4"/>' +
      '</g></svg>'
  };

  function sampleMark(sample, index) {
    var art = el('span', 'lr-cctv__key-art');
    art.setAttribute('aria-hidden', 'true');
    var svg = SAMPLE_MARKS[sample.id];
    if (svg) art.innerHTML = svg;
    else art.textContent = sample.short || String(index + 1);
    return art;
  }

  function flash(node, cls, ms) {
    if (!node) return;
    node.classList.remove(cls);
    void node.offsetWidth;
    node.classList.add(cls);
    setTimeout(function () { node.classList.remove(cls); }, ms);
  }

  function CameraWall(root, engine) {
    this.root = root;
    this.engine = engine;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.main = root.querySelector('#lr-cctv-main');
    this.mainImg = root.querySelector('#lr-cctv-main-img');
    this.mainOsd = root.querySelector('#lr-cctv-main-osd');
    this.mainName = root.querySelector('#lr-cctv-main-name');
    this.wall = root.querySelector('#lr-cctv-wall');
    this.channels = root.querySelector('#lr-cctv-channels');
    this.roomSwitch = root.querySelector('#lr-cctv-room');
    this.roomBadge = root.querySelector('#lr-cctv-room-badge');
    this.readout = root.querySelector('#lr-cctv-readout');
    this.scan = root.querySelector('#lr-cctv-scan');
    this.scanFill = root.querySelector('#lr-cctv-scan-fill');
    this.play = root.querySelector('#lr-cctv-play');
    this.mute = root.querySelector('#lr-cctv-mute');
    this.vol = root.querySelector('#lr-cctv-vol');
    this.counter = root.querySelector('#lr-cctv-counter');
    this.note = document.getElementById('lr-proto-note');

    this.prevRoom = this.engine.roomIndex;
    this.cutting = false;
    this.flashingAlt = false;
    this.altFlashTimer = null;
    this.muted = false;
    this.lastVol = this.engine.userVolume || 0.7;
    this.duration = 0;

    this.build();
    this.bind();
    this.prefetchAltArt();
    this.setMainPicture(this.engine.getState());
    this.render(this.engine.getState());
    this.startClock();
    this.startDropouts();
    this.engine.prefetchNeighbors();
  }

  /* ─── build ─── */

  CameraWall.prototype.build = function () {
    var self = this;

    if (this.wall) {
      this.wall.innerHTML = '';
      this.engine.rooms().forEach(function (room, index) {
        var mon = el('button', 'lr-cctv__mon lr-cctv__mon--thumb');
        mon.type = 'button';
        mon.setAttribute('aria-label', 'Camera ' + (index + 1) + ', ' + room.name);

        var screen = el('span', 'lr-cctv__screen');
        var img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.width = 160;
        img.height = 120;
        var src = self.engine.artworkUrl(index);
        if (src) img.src = src;
        screen.appendChild(img);
        screen.appendChild(el('span', 'lr-cctv__lines'));
        screen.appendChild(el('span', 'lr-cctv__osd', 'CAM ' + pad2(index + 1)));
        screen.appendChild(el('span', 'lr-cctv__static'));

        var foot = el('span', 'lr-cctv__foot');
        foot.appendChild(el('i', 'lr-cctv__led'));
        foot.appendChild(el('span', 'lr-cctv__num', room.name.toUpperCase()));

        mon.appendChild(screen);
        mon.appendChild(foot);
        mon.addEventListener('click', function () { self.pickCamera(index); });
        self.wall.appendChild(mon);
      });
    }

    if (this.channels) {
      this.channels.innerHTML = '';
      this.engine.samples().forEach(function (sample, index) {
        var key = el('button', 'lr-cctv__key');
        key.type = 'button';
        key.setAttribute('aria-label', 'Channel ' + (index + 1) + ', ' + sample.label);
        key.appendChild(el('i', 'lr-cctv__key-led'));
        key.appendChild(sampleMark(sample, index));
        key.appendChild(el('small', null, sample.label));
        key.addEventListener('click', function () {
          self.engine.unlock();
          self.engine.setSample(index);
        });
        self.channels.appendChild(key);
      });
    }
  };

  /* ─── behaviour ─── */

  CameraWall.prototype.pickCamera = function (index) {
    var engine = this.engine;
    engine.unlock();
    if (engine.dry) engine.setDry(false);
    if (index !== engine.roomIndex) engine.setRoom(index);
  };

  CameraWall.prototype.bind = function () {
    var self = this;
    var engine = this.engine;

    engine.on('change', function (state) { self.render(state); });
    engine.on('time', function (t) { self.renderTime(t); });
    engine.on('roomcut', function (state) { self.cut(state); });
    engine.on('drycut', function (state) { self.cut(state); });
    engine.on('needgesture', function () {
      if (self.note) self.note.textContent = 'press play once to enable sound';
    });

    if (this.roomSwitch) {
      this.roomSwitch.addEventListener('click', function () {
        engine.unlock();
        engine.setDry(!engine.dry);
      });
    }

    if (this.play) {
      this.play.addEventListener('click', function () {
        engine.unlock();
        engine.toggle();
      });
    }

    if (this.mute) {
      this.mute.addEventListener('click', function () {
        engine.unlock();
        if (engine.userVolume === 0) {
          self.muted = false;
          engine.setVolume(engine._lastAudible > 0 ? engine._lastAudible : 0.7);
        } else {
          engine._lastAudible = engine.userVolume;
          self.lastVol = engine.userVolume;
          self.muted = true;
          engine.setVolume(0);
        }
      });
    }

    if (this.vol) {
      this.vol.addEventListener('input', function () {
        engine.unlock();
        var v = parseFloat(self.vol.value);
        self.muted = v === 0;
        if (v > 0) {
          self.lastVol = v;
          engine._lastAudible = v;
        }
        engine.setVolume(v);
      });
    }

    if (this.scan) {
      var seekTo = function (clientX) {
        if (!self.duration) return;
        var r = self.scan.getBoundingClientRect();
        var ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
        engine.unlock();
        engine.seek(ratio * self.duration);
        self.renderTime({ current: ratio * self.duration, duration: self.duration });
      };
      var dragging = false;
      this.scan.addEventListener('pointerdown', function (e) {
        dragging = true;
        self.scan.setPointerCapture(e.pointerId);
        seekTo(e.clientX);
      });
      this.scan.addEventListener('pointermove', function (e) { if (dragging) seekTo(e.clientX); });
      this.scan.addEventListener('pointerup', function () { dragging = false; });
      this.scan.addEventListener('keydown', function (e) {
        var big = 15, small = 5;
        var t = engine.active ? engine.active.currentTime || 0 : 0;
        var handled = true;
        switch (e.key) {
          case 'ArrowRight': case 'ArrowUp': t += small; break;
          case 'ArrowLeft': case 'ArrowDown': t -= small; break;
          case 'PageUp': t += big; break;
          case 'PageDown': t -= big; break;
          case 'Home': t = 0; break;
          case 'End': t = self.duration || 0; break;
          case ' ': case 'Spacebar': case 'Enter':
            e.preventDefault();
            engine.unlock();
            engine.toggle();
            return;
          default: handled = false;
        }
        if (!handled) return;
        e.preventDefault();
        if (!self.duration) return;
        engine.seek(Math.min(self.duration, Math.max(0, t)));
      });
    }
  };

  /* the switcher cut: main → static, then power-on with the new picture */
  CameraWall.prototype.cut = function (state) {
    var self = this;
    var thumbs = this.root.querySelectorAll('.lr-cctv__mon--thumb');
    var outgoing = thumbs[this.prevRoom];
    this.prevRoom = state.roomIndex;

    if (this.reduced || !this.main) {
      this.setMainPicture(state);
      return;
    }

    this.cutting = true;
    if (outgoing && outgoing !== thumbs[state.roomIndex]) flash(outgoing, 'is-static', 120);
    flash(this.main, 'is-static', 120);
    setTimeout(function () {
      self.setMainPicture(self.engine.getState());
      flash(self.main, 'is-poweron', 220);
      self.cutting = false;
    }, 130);
  };

  CameraWall.prototype.setMainPicture = function (state) {
    if (this.flashingAlt) this.clearAltFlash(false);
    if (this.mainImg && state.artwork) {
      if (this.mainImg.getAttribute('src') !== state.artwork) {
        this.mainImg.src = state.artwork;
      }
      this.mainImg.alt = state.room ? state.room.name : '';
    }
    this.root.classList.toggle('is-dry', !!state.dry);
    if (this.mainOsd) {
      this.mainOsd.textContent = state.dry
        ? 'DRY \u00B7 CH ' + (state.sampleIndex + 1)
        : 'CAM ' + pad2(state.roomIndex + 1) + ' \u00B7 CH ' + (state.sampleIndex + 1);
    }
    if (this.mainName) {
      this.mainName.textContent = state.dry
        ? 'NO ROOM \u00B7 ' + (state.sample ? state.sample.label.toUpperCase() : '')
        : (state.room ? state.room.name.toUpperCase() : '\u2014') +
          (state.sample ? ' \u00B7 ' + state.sample.label.toUpperCase() : '');
    }
  };

  CameraWall.prototype.prefetchAltArt = function () {
    var engine = this.engine;
    var rooms = engine.rooms();
    for (var i = 0; i < rooms.length; i++) {
      var url = engine.artworkAltUrl(i);
      if (!url) continue;
      var img = new Image();
      img.src = url;
    }
  };

  CameraWall.prototype.clearAltFlash = function (restore) {
    if (this.altFlashTimer) {
      clearTimeout(this.altFlashTimer);
      this.altFlashTimer = null;
    }
    var was = this.flashingAlt;
    this.flashingAlt = false;
    if (restore && was && this.mainImg) {
      var primary = this.engine.artworkUrl();
      if (primary) this.mainImg.src = primary;
    }
  };

  /* Brief cut to the room's alternate still, then back. */
  CameraWall.prototype.flashAlt = function () {
    var self = this;
    if (this.reduced || this.cutting || this.flashingAlt || !this.mainImg) return;
    var state = this.engine.getState();
    if (state.dry) return;
    var alt = this.engine.artworkAltUrl(state.roomIndex);
    var primary = state.artwork;
    if (!alt || !primary || alt === primary) return;

    this.flashingAlt = true;
    this.mainImg.src = alt;
    this.altFlashTimer = setTimeout(function () {
      self.altFlashTimer = null;
      self.flashingAlt = false;
      if (self.cutting) return;
      var now = self.engine.getState();
      if (now.dry) return;
      var back = self.engine.artworkUrl(now.roomIndex);
      if (back) self.mainImg.src = back;
    }, 90 + Math.floor(Math.random() * 50));
  };

  CameraWall.prototype.startDropouts = function () {
    var self = this;
    if (this.reduced) return;
    var schedule = function () {
      var wait = 5000 + Math.random() * 6000;
      setTimeout(function () {
        var mons = self.root.querySelectorAll('.lr-cctv__mon');
        if (mons.length) flash(mons[Math.floor(Math.random() * mons.length)], 'is-glitch', 80);
        if (Math.random() < 0.25) flash(self.root, 'is-brownout', 70);
        if (Math.random() < 0.45) self.flashAlt();
        schedule();
      }, wait);
    };
    schedule();
  };

  CameraWall.prototype.startClock = function () {
    var self = this;
    var tick = function () {
      var d = new Date();
      var stamp = pad2(d.getHours()) + ':' + pad2(d.getMinutes()) + ':' + pad2(d.getSeconds());
      var stamps = self.root.querySelectorAll('.lr-cctv__stamp');
      for (var i = 0; i < stamps.length; i++) stamps[i].textContent = stamp;
    };
    tick();
    setInterval(tick, 1000);
  };

  /* ─── render ─── */

  CameraWall.prototype.render = function (state) {
    var thumbs = this.root.querySelectorAll('.lr-cctv__mon--thumb');
    for (var i = 0; i < thumbs.length; i++) thumbs[i].classList.toggle('is-live', i === state.roomIndex);

    var keys = this.root.querySelectorAll('.lr-cctv__channels .lr-cctv__key');
    for (var k = 0; k < keys.length; k++) keys[k].classList.toggle('is-on', k === state.sampleIndex);

    if (this.roomSwitch) {
      var roomOn = !state.dry;
      this.roomSwitch.setAttribute('aria-pressed', roomOn ? 'true' : 'false');
      this.roomSwitch.setAttribute(
        'aria-label',
        roomOn ? 'LiminalVerb on. Click to bypass.' : 'LiminalVerb bypassed. Click to hear the room.'
      );
    }
    if (this.roomBadge) this.roomBadge.textContent = state.dry ? 'ROOM OFF' : 'ROOM ON';

    if (!this.cutting) this.setMainPicture(state);

    var active = state.playing || state.loading;
    this.root.classList.toggle('is-playing', !!state.playing);

    if (this.readout && state.room && state.sample) {
      var where = state.dry ? 'DRY' : 'CAM ' + pad2(state.roomIndex + 1) + ' ' + state.room.name.toUpperCase();
      this.readout.textContent = where + ' \u00B7 CH ' + (state.sampleIndex + 1) + ' ' + state.sample.label.toUpperCase() +
        (state.hasAudio ? '' : ' \u00B7 NO SIGNAL');
    }

    if (this.play) {
      this.play.innerHTML = active ? '&#10074;&#10074;' : '&#9658;';
      this.play.setAttribute('aria-label', active ? 'Pause' : 'Play');
      this.play.disabled = !state.hasAudio;
    }

    if (this.mute) this.mute.textContent = this.muted || state.volume === 0 ? '<x ' : '<))';
    if (this.vol) {
      if (document.activeElement !== this.vol) this.vol.value = String(state.volume);
      this.vol.style.setProperty('--vol', (state.volume * 100) + '%');
    }

    if (this.note) {
      var sampleName = state.sample ? state.sample.label : '';
      var where2 = state.dry ? 'dry' : 'in ' + (state.room ? state.room.name : '');
      if (!state.hasAudio) this.note.textContent = sampleName + ' ' + where2 + ' \u2014 preview coming soon';
      else if (state.loading) this.note.textContent = 'cueing ' + sampleName + ' ' + where2 + '\u2026';
      else if (state.playing) this.note.textContent = sampleName + ' \u00B7 ' + (state.dry ? 'dry' : state.room.name);
      else this.note.textContent = '';
    }

    if (!active) this.renderTime({ current: 0, duration: state.duration || 0 });
  };

  CameraWall.prototype.renderTime = function (t) {
    this.duration = t.duration || 0;
    var pct = t.duration > 0 ? (t.current / t.duration) * 100 : 0;
    if (this.scanFill) this.scanFill.style.width = pct + '%';
    if (this.scan) {
      this.scan.setAttribute('aria-valuenow', Math.round(pct));
      this.scan.setAttribute('aria-valuetext', fmt(t.current) + ' of ' + fmt(t.duration));
    }
    if (this.counter) this.counter.textContent = fmt(t.current) + ' / ' + fmt(t.duration);
  };

  function boot() {
    var root = document.getElementById('lr-proto');
    if (!root || root.dataset.ready === 'true') return;
    if (!window.LiminalPreviewEngine || !window.LR_ROOM_DEMOS) {
      setTimeout(boot, 40);
      return;
    }
    root.dataset.ready = 'true';

    var engine = new window.LiminalPreviewEngine({ assetBase: assetBase(), fadeMs: 200 });
    engine._lastAudible = engine.userVolume || 0.7;
    try {
      var q = new URLSearchParams(window.location.search);
      if (q.get('lr_dry') === '1') engine.dry = true;
      if (q.get('lr_dry') === '0') engine.dry = false;
    } catch (err) { /* ignore */ }

    var camera = document.getElementById('lr-cctv');
    if (camera) new CameraWall(camera, engine);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
